/**
 * Agora Real-Time STT — Untertitel-Nachrichten dekodieren.
 *
 * Der STT-Bot published Protobuf-`Text`-Nachrichten als Data-Stream in den
 * RTC-Kanal (Event `stream-message`). Statt einer Protobuf-Bibliothek (die
 * einzige benötigte Message ist winzig) ein handgeschriebener Decoder für
 * genau die Felder aus Agoras SttMessage-Schema:
 *
 *   message Text {
 *     int64 uid = 4;            // Sprecher-UID
 *     repeated Word words = 10; // Transkript-Segmente
 *     string data_type = 13;    // "transcribe" | "translate"
 *     repeated Translation trans = 14;
 *     string culture = 15;      // Sprachcode des Transkripts
 *   }
 *   message Word { string text = 1; bool is_final = 4; }
 *   message Translation { bool is_final = 1; string lang = 2; repeated string texts = 3; }
 *
 * Unbekannte Felder werden anhand des Wire-Types übersprungen — neue Felder
 * von Agora brechen den Decoder damit nicht.
 */

export interface CaptionMessage {
  /** UID des Sprechers, dessen Audio transkribiert wurde. */
  speakerUid: number;
  /** 'transcribe' (Originaltext) oder 'translate' (Übersetzungen). */
  dataType: string;
  /** Zusammengesetzter Transkripttext (bei data_type=transcribe). */
  text: string;
  /** Ist das Segment abgeschlossen (Satzende) oder noch im Fluss? */
  isFinal: boolean;
  /** Sprachcode des Transkripts (z. B. "de-DE"). */
  culture: string;
  /** Übersetzungen (bei data_type=translate). */
  translations: { lang: string; text: string; isFinal: boolean }[];
}

const td = new TextDecoder();

/** Varint lesen → [wert, neue Position]. 53-Bit reicht (UIDs < 2^32). */
function varint(b: Uint8Array, p: number): [number, number] {
  let result = 0;
  let shift = 0;
  while (p < b.length) {
    const byte = b[p++];
    result += (byte & 0x7f) * 2 ** shift;
    if ((byte & 0x80) === 0) return [result, p];
    shift += 7;
  }
  return [result, p];
}

/** Feld anhand des Wire-Types überspringen. */
function skip(b: Uint8Array, p: number, wire: number): number {
  if (wire === 0) return varint(b, p)[1];
  if (wire === 1) return p + 8;
  if (wire === 2) { const [len, np] = varint(b, p); return np + len; }
  if (wire === 5) return p + 4;
  return b.length; // unbekannter Wire-Type: Rest verwerfen
}

function parseWord(b: Uint8Array): { text: string; isFinal: boolean } {
  let p = 0;
  let text = '';
  let isFinal = false;
  while (p < b.length) {
    const [tag, np] = varint(b, p);
    p = np;
    const field = Math.floor(tag / 8);
    const wire = tag % 8;
    if (field === 1 && wire === 2) {
      const [len, sp] = varint(b, p);
      text = td.decode(b.subarray(sp, sp + len));
      p = sp + len;
    } else if (field === 4 && wire === 0) {
      const [v, vp] = varint(b, p);
      isFinal = v !== 0;
      p = vp;
    } else {
      p = skip(b, p, wire);
    }
  }
  return { text, isFinal };
}

function parseTranslation(b: Uint8Array): { lang: string; text: string; isFinal: boolean } {
  let p = 0;
  let lang = '';
  let isFinal = false;
  const texts: string[] = [];
  while (p < b.length) {
    const [tag, np] = varint(b, p);
    p = np;
    const field = Math.floor(tag / 8);
    const wire = tag % 8;
    if (field === 1 && wire === 0) {
      const [v, vp] = varint(b, p);
      isFinal = v !== 0;
      p = vp;
    } else if (field === 2 && wire === 2) {
      const [len, sp] = varint(b, p);
      lang = td.decode(b.subarray(sp, sp + len));
      p = sp + len;
    } else if (field === 3 && wire === 2) {
      const [len, sp] = varint(b, p);
      texts.push(td.decode(b.subarray(sp, sp + len)));
      p = sp + len;
    } else {
      p = skip(b, p, wire);
    }
  }
  return { lang, text: texts.join(''), isFinal };
}

/**
 * Eine Stream-Message des STT-Bots dekodieren.
 * Gibt null zurück, wenn die Nachricht kein verwertbarer Untertitel ist —
 * defensive Auslegung, damit fremde Data-Stream-Nutzung nie crasht.
 */
export function decodeCaption(payload: Uint8Array): CaptionMessage | null {
  try {
    let p = 0;
    let speakerUid = 0;
    let dataType = '';
    let culture = '';
    const words: { text: string; isFinal: boolean }[] = [];
    const translations: { lang: string; text: string; isFinal: boolean }[] = [];

    while (p < payload.length) {
      const [tag, np] = varint(payload, p);
      p = np;
      const field = Math.floor(tag / 8);
      const wire = tag % 8;
      if (field === 4 && wire === 0) {
        const [v, vp] = varint(payload, p);
        speakerUid = v;
        p = vp;
      } else if (field === 10 && wire === 2) {
        const [len, sp] = varint(payload, p);
        words.push(parseWord(payload.subarray(sp, sp + len)));
        p = sp + len;
      } else if (field === 13 && wire === 2) {
        const [len, sp] = varint(payload, p);
        dataType = td.decode(payload.subarray(sp, sp + len));
        p = sp + len;
      } else if (field === 14 && wire === 2) {
        const [len, sp] = varint(payload, p);
        translations.push(parseTranslation(payload.subarray(sp, sp + len)));
        p = sp + len;
      } else if (field === 15 && wire === 2) {
        const [len, sp] = varint(payload, p);
        culture = td.decode(payload.subarray(sp, sp + len));
        p = sp + len;
      } else {
        p = skip(payload, p, wire);
      }
    }

    if (!words.length && !translations.length) return null;
    return {
      speakerUid,
      dataType: dataType || (translations.length ? 'translate' : 'transcribe'),
      text: words.map((w) => w.text).join(''),
      isFinal: words.length > 0 && words.every((w) => w.isFinal),
      culture,
      translations,
    };
  } catch {
    return null;
  }
}
